import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmail, getSessionUser } from '../services/supabase';
import './AdminLogin.css';

const AdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // First try normal Supabase sign-in
      try {
        // Quick server-side check: is this email present in users table with role = 'admin'?
        try {
          const checkRes = await fetch('/api/check-admin-email', {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: formData.email })
          });
          if (checkRes.ok) {
            const { isAdmin } = await checkRes.json();
            if (!isAdmin) {
              console.warn('[AdminLogin] Email does not appear to be an admin; skipping Supabase admin sign-in path');
              // fall through to app-level password check
              throw new Error('Not an admin email');
            }
          } else {
            console.warn('[AdminLogin] check-admin-email returned non-ok', await checkRes.text());
          }
        } catch (checkErr) {
          console.warn('[AdminLogin] admin-check failed, continuing to try Supabase sign-in as fallback', checkErr);
        }

        console.info('[AdminLogin] Attempting Supabase sign-in for', { email: formData.email });
        await signInWithEmail(formData.email, formData.password);
        console.info('[AdminLogin] Supabase sign-in successful for', { email: formData.email });

        // Fetch profile row to check role; App relies on the profile.role === 'admin'
        try {
          const { profile } = await getSessionUser();
          console.info('[AdminLogin] post-signin profile:', { role: profile?.role ?? null, id: profile?.id ?? null });
          if (profile?.role === 'admin') {
            // notify the app and navigate
            try { window.dispatchEvent(new Event('app:admin-flag')); } catch (e) { /* ignore */ }
            navigate('/admin');
            return;
          }
          // If not admin, fall through to the fallback check below
          console.warn('[AdminLogin] Signed-in account does not have admin role in users table; falling back to app-level admin password check');
        } catch (profErr) {
          console.warn('[AdminLogin] Failed to fetch profile after sign-in', profErr);
        }
      } catch (supErr: any) {
        console.warn('[AdminLogin] Supabase sign-in failed or pre-check indicated not admin; trying app-level admin password check', supErr?.message || supErr);
      }

      // App-level admin password check (less secure, for local testing)
      const adminPass = process.env.REACT_APP_ADMIN_PASSWORD || process.env.ADMIN_PASSWORD;
      if (formData.password === adminPass) {
        // Store a simple flag in sessionStorage to indicate admin access (not secure)
        sessionStorage.setItem('isAdmin', '1');
        // Notify app to re-evaluate admin status immediately
        try { window.dispatchEvent(new Event('app:admin-flag')); } catch (e) { /* ignore */ }
        navigate('/admin');
        return;
      }
      throw new Error('Invalid credentials');
    } catch (error: any) {
      console.error('Login error:', error);
      setError(error.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login">
      <div className="login-container">
        <div className="login-header">
          <h1 className="login-title">Admin Portal</h1>
          <p className="login-description">
            Sign in to access the admin dashboard
          </p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email" className="form-label">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="form-input"
              placeholder="admin@dcdetails.com"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter your password"
              required
            />
          </div>

          <button
            type="submit"
            className="login-button"
            disabled={loading}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        {/* Demo credentials removed for production security */}
      </div>
    </div>
  );
};

export default AdminLogin;